import { Controller } from "@hotwired/stimulus"

// chart-hover — tooltip + realce por ponto num gráfico SVG (linha/área), CSP-safe
// (sem lib externa, sem handler inline). Os pontos vêm do servidor: cada marcador
// é um <circle data-chart-hover-target="point"> com data-label (mês) e data-value
// (valor já formatado em R$). O controller NÃO recalcula nada — só exibe o que o
// servidor mandou. Respeita prefers-reduced-motion (via CSS no template).
//
// Estrutura esperada no DOM:
//   <div data-controller="chart-hover" class="relative">
//     <svg ...>
//       <line data-chart-hover-target="guide" .../>        (opcional: linha-guia vertical)
//     </svg>
//     <button data-chart-hover-target="point"                (1 por ponto, sobre o SVG em px)
//             data-cx="..." data-left="..." data-top="..."   (left/top em %, pra mover o dot)
//             data-label="set/2026" data-value="R$ 53.272,39" .../>
//     <div data-chart-hover-target="dot" ... hidden></div>    (realce que segue o cursor)
//     <div data-chart-hover-target="tooltip" role="status" ... hidden>...</div>
//   </div>
// O realce do ponto sob o cursor é: linha-guia vertical (guide) + dot em px + tooltip.
// O dot é um <div> em px (não um <circle>) porque o SVG usa preserveAspectRatio="none"
// (que deformaria círculos). O endpoint visível é desenhado no template.
export default class extends Controller {
  static targets = [ "point", "tooltip", "guide", "dot" ]

  connect() {
    this.pointTargets.forEach((pt) => {
      pt.addEventListener("mouseenter", this.showFor)
      pt.addEventListener("mousemove", this.showFor)
      pt.addEventListener("focus", this.showFor)
      pt.addEventListener("mouseleave", this.hide)
      pt.addEventListener("blur", this.hide)
    })
  }

  disconnect() {
    this.pointTargets.forEach((pt) => {
      pt.removeEventListener("mouseenter", this.showFor)
      pt.removeEventListener("mousemove", this.showFor)
      pt.removeEventListener("focus", this.showFor)
      pt.removeEventListener("mouseleave", this.hide)
      pt.removeEventListener("blur", this.hide)
    })
  }

  showFor = (event) => {
    const pt = event.currentTarget
    const cx = parseFloat(pt.dataset.cx)
    const label = pt.dataset.label || ""
    const value = pt.dataset.value || ""

    // Linha-guia vertical no x do ponto (coordenada no espaço do viewBox do SVG).
    // A guia é vertical → não deforma com preserveAspectRatio="none".
    if (this.hasGuideTarget && !Number.isNaN(cx)) {
      this.guideTarget.setAttribute("x1", cx)
      this.guideTarget.setAttribute("x2", cx)
      this.guideTarget.removeAttribute("hidden")
    }

    // Dot de realce: posiciona no ponto ativo (left/top em %, vindos do servidor).
    if (this.hasDotTarget) {
      this.dotTarget.style.left = pt.style.left
      this.dotTarget.style.top = pt.style.top
      this.dotTarget.removeAttribute("hidden")
    }

    if (this.hasTooltipTarget) {
      this.tooltipTarget.querySelector("[data-chart-hover-label]").textContent = label
      this.tooltipTarget.querySelector("[data-chart-hover-value]").textContent = value
      this.tooltipTarget.removeAttribute("hidden")
      this._position(pt)
    }
  }

  hide = () => {
    if (this.hasGuideTarget) this.guideTarget.setAttribute("hidden", "")
    if (this.hasDotTarget) this.dotTarget.setAttribute("hidden", "")
    if (this.hasTooltipTarget) this.tooltipTarget.setAttribute("hidden", "")
  }

  // Posiciona o tooltip (px, relativo ao wrapper) sobre o ponto sob o cursor.
  // Converte a coordenada do ponto no SVG para px do container, centraliza e
  // clampeia nas bordas pra não vazar.
  _position(pt) {
    const rect = pt.getBoundingClientRect()
    const host = this.element.getBoundingClientRect()
    const tip = this.tooltipTarget
    const cxPx = rect.left + rect.width / 2 - host.left
    const cyPx = rect.top + rect.height / 2 - host.top

    tip.style.left = "0px" // reset pra medir a largura real
    const tw = tip.offsetWidth
    const th = tip.offsetHeight
    let left = cxPx - tw / 2
    left = Math.max(4, Math.min(left, host.width - tw - 4))
    // Acima do ponto por padrão; se não couber, joga pra baixo.
    let top = cyPx - th - 10
    if (top < 4) top = cyPx + 12

    tip.style.left = `${left}px`
    tip.style.top = `${top}px`
  }
}
